'use client';

import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  uploadKycDocument,
  validateFile,
  getDocumentTypeLabel,
  formatFileSize,
  type DocumentUploadProgress,
  type KycDocument,
} from '@/lib/api/kyc-documents';

interface DocumentUploaderProps {
  documentType: string;
  label?: string;
  description?: string;
  isRequired?: boolean;  // Control asterisk display independently
  existingDocument?: KycDocument | null;
  onUploadSuccess?: (document: KycDocument) => void;
  onUploadError?: (error: string) => void;
  disabled?: boolean;
}

/**
 * Fix filename encoding issues (UTF-8 mojibake)
 * Handles cases where Thai characters appear as garbled text like "à¸ à¸²à¸à¸"
 */
function fixFilenameEncoding(filename: string): string {
  if (!filename) return '';

  // Try to detect and fix UTF-8 mojibake
  try {
    // If the string contains mojibake patterns (multiple consecutive high bytes)
    if (/[\xE0-\xFF][\x80-\xBF]/.test(filename)) {
      // Attempt to re-encode as UTF-8
      const buffer = Buffer.from(filename, 'latin1');
      const decodedFilename = buffer.toString('utf8');
      
      // Only use the decoded version if it contains Thai characters or looks valid
      if (/[\u0E00-\u0E7F]/.test(decodedFilename)) {
        return decodedFilename;
      }
    }
  } catch (error) {
    console.warn('Error fixing filename encoding:', error);
  }

  return filename;
}

export default function DocumentUploader({
  documentType,
  label,
  description,
  isRequired = true,  // Default true for backwards compatibility, set to false for optional fields
  existingDocument,
  onUploadSuccess,
  onUploadError,
  disabled = false,
}: DocumentUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<DocumentUploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentLabel = label || getDocumentTypeLabel(documentType);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error || 'ไฟล์ไม่ถูกต้อง');
      return;
    }

    setError(null);
    setSuccess(false);
    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }

    // Auto-upload after file selection
    setTimeout(() => {
      handleUploadInternal(file);
    }, 100);
  };

  const handleUploadInternal = async (file?: File) => {
    const fileToUpload = file || selectedFile;
    if (!fileToUpload) {
      setError('กรุณาเลือกไฟล์');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await uploadKycDocument(
        fileToUpload,
        documentType,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      setSuccess(true);
      setUploadProgress(null);
      
      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }

      // Reset after success
      setTimeout(() => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setSuccess(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);

    } catch (err: any) {
      const errorMessage = err.message || 'เกิดข้อผิดพลาดในการอัพโหลด';
      setError(errorMessage);
      setUploadProgress(null);
      
      // Call error callback
      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    await handleUploadInternal();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    setSuccess(false);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      const fakeEvent = {
        target: { files: [file] },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(fakeEvent);
    }
  };

  return (
    <div className="space-y-4">
      {/* Label and Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {documentLabel}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
        {description && (
          <p className="text-sm text-gray-500">{description}</p>
        )}
      </div>

      {/* Existing Document Status */}
      {existingDocument && !selectedFile && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            อัพโหลดแล้ว: {fixFilenameEncoding(existingDocument.original_file_name)} ({formatFileSize(existingDocument.file_size)})
            {existingDocument.upload_status === 'verified' && ' - ตรวจสอบแล้ว'}
          </AlertDescription>
        </Alert>
      )}

      {/* Upload Area */}
      {!selectedFile && !existingDocument && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            disabled 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : 'border-gray-300 hover:border-blue-400 bg-white cursor-pointer'
          }`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFileSelect}
            disabled={disabled}
          />
          <Upload className={`mx-auto h-12 w-12 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
          <p className={`mt-2 text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
            คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวางที่นี่
          </p>
          <p className="mt-1 text-xs text-gray-500">
            รองรับ: JPEG, PNG, PDF (สูงสุด 10MB)
          </p>
        </div>
      )}

      {/* File Preview */}
      {selectedFile && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start space-x-4">
              {/* Preview Thumbnail */}
              <div className="flex-shrink-0">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="h-20 w-20 object-cover rounded border"
                  />
                ) : (
                  <div className="h-20 w-20 bg-gray-100 rounded border flex items-center justify-center">
                    <FileText className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatFileSize(selectedFile.size)} • {selectedFile.type}
                </p>

                {/* Upload Progress */}
                {uploading && uploadProgress && (
                  <div className="mt-2">
                    <Progress value={uploadProgress.percentage} className="h-2" />
                    <p className="text-xs text-gray-500 mt-1">
                      กำลังอัพโหลด... {uploadProgress.percentage}%
                    </p>
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div className="mt-2 flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span className="text-sm">อัพโหลดสำเร็จ!</span>
                  </div>
                )}
              </div>

              {/* Remove Button */}
              {!uploading && (
                <button
                  onClick={handleRemoveFile}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                  disabled={disabled}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Upload Button */}
            {!uploading && !success && (
              <div className="mt-4">
                <Button
                  onClick={handleUpload}
                  disabled={disabled || !selectedFile}
                  className="w-full"
                >
                  {disabled ? 'ไม่สามารถอัพโหลดได้' : 'อัพโหลดไฟล์'}
                </Button>
              </div>
            )}

            {/* Uploading State */}
            {uploading && (
              <div className="mt-4">
                <Button disabled className="w-full">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  กำลังอัพโหลด...
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
